/**
 * Shared utilities + small presentational components for the Break
 * Management section (PR 1, Phase B1).
 *
 * Lives in one file so the three pages (History / Active / Flagged)
 * stay visually consistent without duplicating helpers. Kept under
 * 100 lines per project convention; presentational only — no data
 * fetching, no routing.
 */

import React from 'react';
import { Badge } from '../../ui/badge';

// Status → label + color. Mirrors the canonical values in
// backend/breaks.py: active, completed, auto_ended, force_ended.
const STATUS_META = {
  active:       { label: 'Active',       cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  completed:    { label: 'Completed',    cls: 'bg-gray-100 text-gray-700 border-gray-200' },
  auto_ended:   { label: 'Auto-Ended',   cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  force_ended:  { label: 'Force-Ended',  cls: 'bg-rose-100 text-rose-800 border-rose-200' },
};

export const BreakStatusBadge = ({ status, flagged, waived }) => {
  const meta = STATUS_META[status] || { label: status || '—', cls: 'bg-gray-100 text-gray-700 border-gray-200' };
  return (
    <div className="flex items-center gap-1.5">
      <Badge variant="outline" className={`${meta.cls} font-medium`} data-testid="break-status-badge">
        {meta.label}
      </Badge>
      {flagged && (
        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 font-medium" data-testid="break-flag-badge">
          Flagged
        </Badge>
      )}
      {waived && (
        <Badge variant="outline" className="bg-sky-50 text-sky-700 border-sky-200 font-medium" data-testid="break-grace-badge">
          Grace — Not Counted
        </Badge>
      )}
    </div>
  );
};

// Human-friendly duration. Handles unset / active rows gracefully.
export const formatDuration = (minutes) => {
  if (minutes == null || isNaN(minutes)) return '—';
  const m = Math.max(0, Math.round(minutes));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
};

export const BreakDurationCell = ({ minutes, maxMinutes }) => {
  const over = (maxMinutes != null) && (minutes > maxMinutes);
  return (
    <span className={over ? 'text-rose-700 font-semibold' : 'text-gray-800'} data-testid="break-duration-cell">
      {formatDuration(minutes)}
      {maxMinutes != null && (
        <span className="text-gray-400 ml-1 text-xs">/ {formatDuration(maxMinutes)}</span>
      )}
    </span>
  );
};

// Paid / Unpaid pill — frozen on the row at start time.
export const PaidUnpaidPill = ({ isPaid }) => (
  <Badge
    variant="outline"
    className={isPaid
      ? 'bg-blue-50 text-blue-700 border-blue-200'
      : 'bg-orange-50 text-orange-700 border-orange-200'}
    data-testid="break-paid-pill"
  >
    {isPaid ? 'Paid' : 'Unpaid'}
  </Badge>
);

// ET-formatted timestamp. Lazy-imported from existing project helper
// to avoid duplicating the timezone-conversion logic.
export { formatDateTimeShortET } from '../../../utils/formatters';
